import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import parse from 'html-react-parser';
import moment from 'moment';
import {
  animationScrollToTop,
  convertHtmlToString,
  convertMessageToHtmlAndTrimSpaces,
  getQuillTextLength,
  isJustNowMessageCheck,
  shortenFilename
} from '../../utils';
import { ReactComponent as LoadIcon } from 'assets/LoadCircle.svg';
import { ReactComponent as AttachIcon } from 'assets/iconButtons/attach-doc-new.svg';
import { ReactComponent as SendIcon } from 'assets/iconButtons/send-button-new.svg';
import { ReactComponent as RemoveFileIcon } from 'assets/iconButtons/remove-file.svg';
import i18nContext from 'components/i18n-context';
import { getCustomerLink, messageRegExp, convertBytesToMegabytes, prepareFieldErrors } from 'services/utils';
import { getCustomerInfoRequest } from 'services/requestAgent';
import { MAX_MESSAGE_LENGTH, MAX_UPLOADED_FILES_SIZE, MESSAGE_TOPIC_STATUS } from 'components/constants';
import Loader from 'components/Loader';
import OperatorsAsyncSelect from 'components/OperatorsAsyncSelect';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';

import Input from 'uikit/Input/Input';
import './Chat.scss';

const Chat = ({
  topic,
  messages,
  isLoading,
  isFileUploading,
  error,
  clearError,
  setNewMessageData,
  uploadedFiles,
  uploadDocuments,
  removeAttachment,
  downloadAttachment,
  topicsRef,
  canWriteMessages,
  clearUploadedFiles,
  isNewMessageCreated,
  setIsNewMessageCreated,
  onSendMessage,
  updateTopicOwnerOperator,
  updateTopicStatus,
  getCurrentTopicMessagesFilesUrls,
  headerText,
  messagesStore
}) => {
  const i18n = useContext(i18nContext);
  const { id } = useParams();
  const serverFieldsErrors = prepareFieldErrors(i18n, error);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const chatRef = useRef(null);
  const [messageValue, setMessageValue] = useState('');
  const previousIdRef = useRef(null);
  const hasScrolledRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [hoveredFileId, setHoveredFileId] = useState(null);
  const [fileError, setFileError] = useState(null);
  // States for pagination
  const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
  const hasTriedLoadingRef = useRef(false);
  const previousScrollHeightRef = useRef(0);

  // Topic status options
  const messagesTopicStatusesOptions = Object.keys(MESSAGE_TOPIC_STATUS).map((status) => {
    return {
      key: MESSAGE_TOPIC_STATUS[status],
      value: i18n.getMessage('messages.topics.status.' + MESSAGE_TOPIC_STATUS[status])
    };
  });

  // Handler functions for updating topic owner and status
  const handleChangeOwnerOperator = async (value) => {
    if (updateTopicOwnerOperator) {
      await updateTopicOwnerOperator(id, value?.id);
    }
  };

  const handleChangeTopicStatus = async (name, value) => {
    if (updateTopicStatus) {
      await updateTopicStatus(id, value);
    }
  };

  // Function to format date and time
  const formatMessageDateTime = (dateStr) => {
    const date = moment(dateStr);
    const formattedDate = date.format('DD.MM.YY');
    const formattedTime = date.format('HH:mm');
    return { date: formattedDate, time: `${formattedTime} GB` };
  };

  // Function to get day label for date separators
  const getDayLabel = (dateStr) => {
    const messageDate = moment(dateStr).startOf('day');
    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'day').startOf('day');
    const currentYear = moment().year();
    const messageYear = messageDate.year();

    if (messageDate.isSame(today)) {
      return i18n.getMessage('messages.date.today');
    } else if (messageDate.isSame(yesterday)) {
      return i18n.getMessage('messages.date.yesterday');
    } else {
      // Format as "Month Day" or "Month Day, Year"
      const monthDay = messageDate.format('MMMM D');
      if (messageYear === currentYear) {
        return monthDay; // E.g.: "December 10"
      } else {
        return `${monthDay}, ${messageYear}`; // E.g.: "December 10, 2024"
      }
    }
  };

  // Function to check if date separator should be shown
  const shouldShowDateSeparator = (currentMessage, previousMessage) => {
    if (!previousMessage) return true; // First message always shows date

    const currentDate = moment(
      currentMessage.createdAt || currentMessage.created_at || currentMessage.create_at
    ).startOf('day');
    const previousDate = moment(
      previousMessage.createdAt || previousMessage.created_at || previousMessage.create_at
    ).startOf('day');

    return !currentDate.isSame(previousDate);
  };

  // Files are handled through messages directly
  // useEffect(() => {
  //   getCurrentTopicMessagesFilesUrls();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [messages]);

  // Load file URLs when messages change
  useEffect(() => {
    if (messages && messages.length > 0 && getCurrentTopicMessagesFilesUrls) {
      getCurrentTopicMessagesFilesUrls();
    }
  }, [messages, getCurrentTopicMessagesFilesUrls]);

  // Reset state when chat changes and load latest messages
  useEffect(() => {
    if (id && id !== previousIdRef.current && messagesStore) {
      previousIdRef.current = id;
      hasScrolledRef.current = false;
      hasTriedLoadingRef.current = false;

      // Reset pagination and load latest page
      messagesStore.resetMessagesPagination();
      messagesStore.getTopicMessages(id, 'latest').then(() => {
        // Scroll to bottom after loading latest messages
        setTimeout(() => {
          if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
            hasScrolledRef.current = true;
          }
        }, 100);
      });
    }
  }, [id, messagesStore]);

  // Check if there are more messages to load
  const hasMoreMessages = messagesStore?.messagesPagination && !messagesStore.messagesPagination.isFirstPage;

  // Ref to store scroll adjustment info
  const scrollAdjustmentRef = useRef(null);

  // Effect to handle scroll position adjustment after new messages
  useEffect(() => {
    if (scrollAdjustmentRef.current && chatRef.current) {
      const { prevHeight, prevTop } = scrollAdjustmentRef.current;
      const newHeight = chatRef.current.scrollHeight;
      const heightDiff = newHeight - prevHeight;

      // Instantly adjust scroll position without animation
      chatRef.current.style.scrollBehavior = 'auto';
      chatRef.current.scrollTop = prevTop + heightDiff;

      // Clear the adjustment info
      scrollAdjustmentRef.current = null;

      // Re-enable smooth scrolling after a brief delay
      setTimeout(() => {
        if (chatRef.current) {
          chatRef.current.style.scrollBehavior = '';
        }
      }, 50);
    }
  }, [messages]); // Re-run when messages change

  // Scroll handler for loading older messages
  const handleScroll = useCallback(
    (e) => {
      const container = e.target;
      const { scrollTop, scrollHeight } = container;
      const scrollThreshold = 50;
      const resetThreshold = 200;

      // Reset flag when scrolled down
      if (scrollTop > resetThreshold && hasTriedLoadingRef.current) {
        hasTriedLoadingRef.current = false;
      }

      // Load when approaching top
      if (
        scrollTop <= scrollThreshold &&
        hasMoreMessages &&
        !isLoadingPrevious &&
        !messagesStore?.isLoadingOlderMessages &&
        !hasTriedLoadingRef.current
      ) {
        setIsLoadingPrevious(true);
        hasTriedLoadingRef.current = true;

        // Save scroll position for later adjustment
        scrollAdjustmentRef.current = {
          prevHeight: scrollHeight,
          prevTop: scrollTop
        };

        messagesStore
          .getTopicMessages(id, 'previous')
          .then(() => {
            // The useEffect above will handle the scroll adjustment
            setIsLoadingPrevious(false);
          })
          .catch(() => {
            scrollAdjustmentRef.current = null;
            setIsLoadingPrevious(false);
            hasTriedLoadingRef.current = false;
          });
      }
    },
    [hasMoreMessages, isLoadingPrevious, messagesStore, id]
  );

  const form = useFormik({
    validateOnChange: false,
    initialValues: {
      message: ''
    },
    validationSchema: Yup.object({
      message: Yup.string()
        .required(i18n.getMessage('messages.error.message.empty'))
        .test('not-only-spaces', i18n.getMessage('messages.error.message.empty'), (value) => {
          return value && value.trim().length > 0;
        })
        .max(MAX_MESSAGE_LENGTH, i18n.getMessage('messages.error.message.maxSize', { size: MAX_MESSAGE_LENGTH }))
        .matches(messageRegExp, i18n.getMessage('messages.error.message.invalidFormat'))
    }),
    onSubmit: () => {
      setNewMessageData(convertMessageToHtmlAndTrimSpaces(messageValue));
      onSendMessage(topic.id);
    }
  });

  const { values, errors, handleSubmit, validateField, submitCount, setFieldValue, resetForm } = form;

  useEffect(() => {
    if (id) {
      resetForm();
      setMessageValue('');
      clearUploadedFiles();
    }
  }, [id]);

  useEffect(() => {
    setFieldValue('message', convertHtmlToString(messageValue));
  }, [messageValue, setFieldValue]);

  const handleMouseEnter = (index) => {
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  const onBlurMessageField = () => {
    validateField('message');
    if (serverFieldsErrors?.message) {
      clearError();
    }
  };

  useEffect(() => {
    if (isNewMessageCreated) {
      // Scroll to bottom instantly when a new message is created
      if (chatRef.current) {
        chatRef.current.scrollTop = chatRef.current.scrollHeight;
      }
      animationScrollToTop(topicsRef);
      setHoveredIndex(null);
      setFieldValue('message', '');
      setMessageValue('');
      resetForm();
      setIsNewMessageCreated(false);
    }
  }, [isNewMessageCreated]);

  // Add drag and drop handlers for textarea
  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isFileUploading && canWriteMessages) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (!isFileUploading && canWriteMessages) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const validationResult = validateFiles(droppedFiles);

      if (validationResult.error) {
        setFileError(validationResult.error);
        // Clear error after 5 seconds
        setTimeout(() => setFileError(null), 5000);
        return;
      }

      setFileError(null);
      const fakeEvent = {
        target: {
          files: droppedFiles
        }
      };
      uploadDocuments(fakeEvent);
    }
  };

  const handleAttachClick = () => {
    if (fileInputRef.current && !isFileUploading) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validationResult = validateFiles(files);

    if (validationResult.error) {
      setFileError(validationResult.error);
      // Clear error after 5 seconds
      setTimeout(() => setFileError(null), 5000);
      return;
    }

    setFileError(null);
    uploadDocuments(e);
  };

  // Make entire textarea clickable
  const handleTextAreaClick = (e) => {
    // Prevent clicks on toolbar buttons from triggering this
    if (e.target.closest('.ql-toolbar')) {
      return;
    }

    // Only handle clicks outside the actual editor area
    if (e.target.closest('.ql-editor')) {
      return;
    }

    // Find the quill editor and focus it
    const quilEditor = document.querySelector('.chat-form-input .ql-editor');
    if (quilEditor) {
      quilEditor.focus();
      // Place cursor at the end only if editor is empty or unfocused
      if (quilEditor.textContent.trim().length === 0) {
        const selection = window.getSelection();
        if (selection) {
          const range = document.createRange();
          range.selectNodeContents(quilEditor);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    }
  };

  // Helper functions for file preview
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const truncateFileName = (name, maxLength = 15) => {
    if (!name) return '';
    const extension = name.split('.').pop();
    const nameWithoutExt = name.substring(0, name.lastIndexOf('.'));
    if (name.length <= maxLength) return name;
    const truncatedName = nameWithoutExt.substring(0, 8) + '...' + nameWithoutExt.substring(nameWithoutExt.length - 4);
    return `${truncatedName}.${extension}`;
  };

  const getFilePreview = (file) => {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    const pdfExtensions = ['pdf'];
    const extension = file.name ? file.name.split('.').pop().toLowerCase() : '';

    if (imageExtensions.includes(extension)) {
      return { type: 'image', url: file.url || (file instanceof File ? URL.createObjectURL(file) : null) };
    }
    if (pdfExtensions.includes(extension)) {
      return { type: 'pdf' };
    }
    return { type: 'other', extension };
  };

  // Validate files
  const validateFiles = (filesList) => {
    const maxSize = MAX_UPLOADED_FILES_SIZE;
    const accept = '.pdf,.jpg,.jpeg,.png';
    const acceptedExtensions = accept.split(',').map((ext) => ext.trim().toLowerCase());

    for (const file of filesList) {
      const extension = `.${file.name.split('.').pop().toLowerCase()}`;

      if (!acceptedExtensions.includes(extension)) {
        return {
          error: `File format not supported. Please use: ${accept.replace(/\./g, '').toUpperCase()}`
        };
      }

      if (file.size > maxSize) {
        return {
          error: `File size too large. Maximum size is ${convertBytesToMegabytes(maxSize)}MB`
        };
      }
    }

    return { error: null };
  };

  // Handle redirect to customer
  const handleRedirectToCustomer = async (e, customerNumber) => {
    e.stopPropagation();
    if (!customerNumber) return;

    try {
      const customer = await getCustomerInfoRequest(customerNumber);
      const customerUrl = getCustomerLink(customerNumber, customer.status, customer.type);
      window.open(customerUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to redirect to customer:', error);
    }
  };

  return (
    <div className='chat-wrapper'>
      {/* Header with inline filters */}
      <div className='chat-header-with-filters'>
        <h2 className='chat-header-title' title={headerText}>
          {headerText}
        </h2>
        <div className='chat-header-filters'>
          <div className='filter-item'>
            <OperatorsAsyncSelect
              className='filter-select operator-select'
              handleValueChanged={handleChangeOwnerOperator}
              value={topic?.ownerOperator}
              placeholder={i18n.getMessage('messages.topics.filter.ownerOperator.placeholder')}
              isClearable={false}
            />
          </div>
          <div className='filter-item'>
            <InputDropDown
              className='filter-select status-select'
              id='topicStatus'
              name='topicStatus'
              options={messagesTopicStatusesOptions}
              value={topic?.status}
              onChange={handleChangeTopicStatus}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className='loader-container'>
          <Loader />
        </div>
      ) : (
        <>
          <div
            className={`chat-messages-container ${isLoadingPrevious ? 'loading-older' : ''}`}
            ref={chatRef}
            onScroll={handleScroll}
          >
            {/* Loader for older messages */}
            {messagesStore?.isLoadingOlderMessages && (
              <div className='loader-container-top'>
                <Loader />
              </div>
            )}

            {messages.map((message, index) => {
              // Map backend fields to expected format
              const createdAt = message.createdAt || message.created_at || message.create_at;
              const senderType = message.senderType || message.sender_type;
              const messageBody = message.messageBody || message.body;

              const isJustNowMessageCreated = isJustNowMessageCheck(createdAt);
              const isOperator = senderType === 'OPERATOR';
              const showDateSeparator = shouldShowDateSeparator(message, messages[index - 1]);
              const dateLabel = getDayLabel(createdAt);

              return (
                <React.Fragment key={index}>
                  {showDateSeparator && (
                    <div className='date-separator'>
                      <span className='date-separator-text'>{dateLabel}</span>
                    </div>
                  )}
                  <div className={`chat-message ${isOperator ? 'message-operator' : 'message-user'}`}>
                    <div className='message-bubble'>
                      <div className='message-header'>
                        <span className='message-author'>
                          {isOperator ? (
                            `Operator${message.senderOperatorName ? ` (${message.senderOperatorName})` : ''}`
                          ) : topic?.accountNumber ? (
                            <span
                              className='customer-link'
                              onClick={(e) => handleRedirectToCustomer(e, topic.accountNumber)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleRedirectToCustomer(e, topic.accountNumber);
                                }
                              }}
                              role='button'
                              tabIndex={0}
                              title='Go to customer details'
                            >
                              {topic?.accountName || topic?.accountNumber}
                            </span>
                          ) : (
                            topic?.accountName || 'Customer'
                          )}
                        </span>
                        {isJustNowMessageCreated ? (
                          <div className='message-date'>
                            <span className='message-date-just-now'>{i18n.getMessage('messages.date.justNow')}</span>
                          </div>
                        ) : (
                          <div className='message-date'>
                            <span className='message-date-text'>{formatMessageDateTime(createdAt).date}</span>
                            <span className='message-date-text'>{formatMessageDateTime(createdAt).time}</span>
                          </div>
                        )}
                      </div>
                      <div className='message-body'>{messageBody ? parse(messageBody) : ''}</div>
                      {message.documents && message.documents.length > 0 && (
                        <div className='message-attachments'>
                          <span className='attachments-label'>
                            {i18n.getMessage('messages.attachments.label')} ({message.documents.length})
                          </span>
                          <div className='attachments-list'>
                            {message.documents.map((file, fileIndex) => {
                              const hoveredKey = `${index}-${fileIndex}`;
                              return (
                                <div
                                  key={fileIndex}
                                  className='attachment-item'
                                  onMouseEnter={() => handleMouseEnter(hoveredKey)}
                                  onMouseLeave={handleMouseLeave}
                                >
                                  {!file.url ? (
                                    <div className='attachment-preview'>
                                      <p className='placeholder-wave h-100'>
                                        <span className='placeholder w-100 h-100 skeleton' />
                                      </p>
                                    </div>
                                  ) : (file.contentType || file.content_type) === 'application/pdf' ||
                                    file.name?.toLowerCase().endsWith('.pdf') ? (
                                    <div className='attachment-preview pdf-preview'>
                                      <svg
                                        className='pdf-icon'
                                        width='40'
                                        height='40'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        xmlns='http://www.w3.org/2000/svg'
                                      >
                                        <path
                                          d='M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z'
                                          stroke='#8F27C7'
                                          strokeWidth='1.5'
                                          strokeLinecap='round'
                                          strokeLinejoin='round'
                                        />
                                        <path
                                          d='M14 2V8H20'
                                          stroke='#8F27C7'
                                          strokeWidth='1.5'
                                          strokeLinecap='round'
                                          strokeLinejoin='round'
                                        />
                                        <path d='M9 13H15' stroke='#8F27C7' strokeWidth='1.5' strokeLinecap='round' />
                                        <path d='M9 17H15' stroke='#8F27C7' strokeWidth='1.5' strokeLinecap='round' />
                                      </svg>
                                      <span className='pdf-label'>PDF</span>
                                    </div>
                                  ) : (
                                    <img className='attachment-preview' src={file.url} alt={file.name} />
                                  )}
                                  <div className='attachment-overlay'>
                                    {hoveredIndex === hoveredKey && (
                                      <>
                                        <div className='overlay-top'>
                                          <span className='attachment-name'>{shortenFilename(file.name)}</span>
                                        </div>
                                        <div className='overlay-bottom'>
                                          <button
                                            className='attachment-download-btn'
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              downloadAttachment(file.id);
                                            }}
                                            title={i18n.getMessage('messages.attachments.download')}
                                          >
                                            <LoadIcon />
                                          </button>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                  <div className='attachment-filename'>
                                    <span title={file.name}>{file.name}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {canWriteMessages && (
            <div className='chat-input-container'>
              <form action='#' id='chat-form' onSubmit={handleSubmit} className='chat-form'>
                <div className='chat-input-area'>
                  <div
                    className={`chat-input-wrapper ${isDragging ? 'dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <input
                      ref={fileInputRef}
                      type='file'
                      multiple
                      accept='.pdf,.jpg,.jpeg,.png'
                      onChange={handleFileSelect}
                      disabled={isFileUploading}
                      style={{ display: 'none' }}
                    />

                    <button
                      type='button'
                      className='attach-button'
                      onClick={handleAttachClick}
                      disabled={isFileUploading}
                      title={i18n.getMessage('messages.button.attachFile')}
                    >
                      <AttachIcon />
                    </button>

                    <div
                      className='text-input-area'
                      onClick={handleTextAreaClick}
                      onKeyDown={(e) => {
                        // Don't prevent space or other typing keys
                        if (e.key === 'Enter' && !e.shiftKey) {
                          handleTextAreaClick(e);
                        }
                      }}
                      role='textbox'
                      tabIndex={0}
                    >
                      <Input
                        className='chat-form-input'
                        type={'text-editor-quill'}
                        autoFocus={true}
                        placeholder={'Enter your message...'}
                        name={'message'}
                        isDisabled={isFileUploading}
                        value={messageValue}
                        onChange={setMessageValue}
                        onBlur={onBlurMessageField}
                        initialStatus={submitCount}
                        max={MAX_MESSAGE_LENGTH}
                        rows={4}
                        error={errors?.message || serverFieldsErrors?.message}
                        /* eslint-disable-next-line max-len */
                        symbolsCountText={i18n.getMessage('messages.form.input.message.symbolsLeft', {
                          amount: MAX_MESSAGE_LENGTH - getQuillTextLength(values.message)
                        })}
                      />
                    </div>

                    <button
                      type='submit'
                      className='send-button'
                      disabled={isFileUploading || !messageValue.trim()}
                      title={i18n.getMessage('messages.button.sendMessage')}
                    >
                      {isFileUploading ? <Loader isText={false} /> : <SendIcon />}
                    </button>
                  </div>

                  {/* File error message */}
                  {fileError && <div className='file-error-message'>{fileError}</div>}

                  {/* Files preview below the input */}
                  {uploadedFiles && uploadedFiles.length > 0 && (
                    <div className='files-preview-wrapper'>
                      <div className='files-preview'>
                        {uploadedFiles.map((file) => {
                          const preview = getFilePreview(file);
                          return (
                            <div
                              key={file.id}
                              className='file-card'
                              onMouseEnter={() => setHoveredFileId(file.id)}
                              onMouseLeave={() => setHoveredFileId(null)}
                            >
                              {preview.type === 'pdf' ? (
                                <div className='file-pdf-preview'>
                                  <svg
                                    className='pdf-icon'
                                    width='36'
                                    height='36'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                  >
                                    <path
                                      d='M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z'
                                      stroke='#8F27C7'
                                      strokeWidth='1.5'
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                    />
                                    <path
                                      d='M14 2V8H20'
                                      stroke='#8F27C7'
                                      strokeWidth='1.5'
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                    />
                                    <path d='M9 13H15' stroke='#8F27C7' strokeWidth='1.5' strokeLinecap='round' />
                                    <path d='M9 17H15' stroke='#8F27C7' strokeWidth='1.5' strokeLinecap='round' />
                                  </svg>
                                  <span className='pdf-label'>PDF</span>
                                </div>
                              ) : preview.type === 'image' ? (
                                <img src={preview.url} alt={file.name} className='file-image-background' />
                              ) : (
                                <div className='file-default-background'>
                                  <div className='file-type-icon'>{preview.extension?.toUpperCase()}</div>
                                </div>
                              )}

                              <div className={`file-overlay ${hoveredFileId === file.id ? 'hovered' : ''}`}>
                                {hoveredFileId === file.id && (
                                  <>
                                    <div className='file-info'>
                                      <span className='file-name'>
                                        {truncateFileName(file.name)} ({formatFileSize(file.size)})
                                      </span>
                                    </div>
                                    <div className='file-actions'>
                                      <button
                                        className='file-action-btn delete-btn'
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeAttachment(file.id);
                                        }}
                                        title='Delete file'
                                        type='button'
                                      >
                                        <RemoveFileIcon />
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
};

Chat.propTypes = {
  topic: PropTypes.object,
  messages: PropTypes.array.isRequired,
  headerText: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  isFileUploading: PropTypes.bool.isRequired,
  error: PropTypes.any,
  clearError: PropTypes.func.isRequired,
  setNewMessageData: PropTypes.func.isRequired,
  uploadedFiles: PropTypes.array,
  uploadDocuments: PropTypes.func.isRequired,
  removeAttachment: PropTypes.func.isRequired,
  downloadAttachment: PropTypes.func.isRequired,
  topicsRef: PropTypes.any.isRequired,
  canWriteMessages: PropTypes.bool.isRequired,
  clearUploadedFiles: PropTypes.func.isRequired,
  isNewMessageCreated: PropTypes.bool.isRequired,
  setIsNewMessageCreated: PropTypes.func.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  updateTopicOwnerOperator: PropTypes.func,
  updateTopicStatus: PropTypes.func,
  getCurrentTopicMessagesFilesUrls: PropTypes.func,
  messagesStore: PropTypes.object
};

export default inject((stores) => ({
  messagesStore: stores.messagesStore
}))(observer(Chat));
